// Securities picked in the Watchlist grid's "+ Add security" row and not saved
// yet. Save turns them into ONE staged Watchlist change, exactly as the Add
// Security dialog stages its basket:
// - listing picks (a search result, or a listing an identifier lookup found)
//   become the `items` of one stageSecurityResolutionAdditions call;
// - unresolved picks (an identifier MSI has no listing for yet) each go
//   through stageSecurityResolutionUnresolvedAddition, which amends the same
//   pending draft, so the user still reviews and applies one change.
//
// A pick carries only the server's signed tokens and display text. Nothing
// here derives identity: MSI resolves every pick again when it is staged and
// applied. Each change of the picks is one undo step in the owner's draft
// history (pickHistoryAction).

// The server's default cap (services/watchlist_revision_service.py); the
// capacity a discovery search returns is authoritative when known.
export const DEFAULT_MAX_SECURITIES = 500;
// One Stage call takes at most 100 preview groups (MAX_ADD_PREVIEW_GROUPS),
// the ticker basket's limit too.
export const MAX_PICKS_PER_SAVE = 100;

export const EMPTY_PICKS = Object.freeze([]);

export const PICK_OUTCOME = Object.freeze({
    ADDED: 'added',
    // Another listing of a security (or identifier) already picked: the new
    // choice replaces it, one listing per security as in the ticker basket.
    REPLACED: 'replaced',
    DUPLICATE: 'duplicate',
    ALREADY_IN_WATCHLIST: 'already_in_watchlist',
    WATCHLIST_FULL: 'watchlist_full',
    SAVE_FIRST: 'save_first',
    INVALID: 'invalid',
});

// Every identifier type the Watchlist accepts, as both add surfaces name it.
const IDENTIFIER_TYPE_LABELS = Object.freeze({
    sedol: 'SEDOL',
    cusip: 'CUSIP',
    isin: 'ISIN',
    ticker: 'Ticker',
    bloomberg_ticker: 'Bloomberg ticker',
    openfigi_figi: 'OpenFIGI listing FIGI',
    openfigi_composite: 'OpenFIGI composite FIGI',
    openfigi_share_class: 'OpenFIGI share-class FIGI',
    barra_localid: 'Barra LOCALID',
    barrid: 'Barra BARRID',
    axioma_id: 'Axioma ID',
});

export function identifierTypeLabel(identifierType) {
    const type = String(identifierType || '').trim().toLowerCase();
    return IDENTIFIER_TYPE_LABELS[type] || type.toUpperCase();
}

function text(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function joinLabel(ticker, companyName) {
    return [ticker, companyName].filter(Boolean).join(' · ');
}

// The Cognive ID travels with a pick only when the server sent one. Today's
// discovery and preview contracts do not, so the server's `already_active`
// flag is what keeps current members out.
function cogniveIdField(source) {
    const cogniveId = text(source?.cognive_id);
    return cogniveId ? { cogniveId } : {};
}

/** The pick for one Watchlist security discovery result, or null. */
export function pickFromDiscoveryResult(result) {
    const basketKey = text(result?.basket_key);
    const previewToken = text(result?.preview_token);
    const optionToken = text(result?.option_token);
    const ticker = text(result?.ticker);
    const country = text(result?.country);
    if (!basketKey || !previewToken || !optionToken || !ticker || !country) return null;
    if (result.already_active === true) return null;
    const companyName = text(result.display_name);
    return Object.freeze({
        key: `security:${basketKey}`,
        // Discovery returns one result per security and country.
        listingKey: `security:${basketKey}:${country}`,
        kind: 'listing',
        label: joinLabel(ticker, companyName),
        ticker,
        companyName,
        country,
        exchange: text(result.exchange) || text(result.mic),
        previewToken,
        optionTokens: Object.freeze([optionToken]),
        ...cogniveIdField(result),
    });
}

function identifierOf(preview) {
    return {
        type: text(preview?.identifier_type).toLowerCase(),
        value: text(preview?.identifier_value_norm),
    };
}

/**
 * The pick for one selectable listing of an identifier lookup
 * (previewSecurityResolutionAdd), or null. Like the dialog's single choice,
 * one listing per looked-up identifier. A listing with the server's basket
 * key is keyed like a search result, so a security found both ways is one
 * pick: the same listing again is a duplicate, another listing replaces it.
 */
export function pickFromIdentifierOption(preview, option) {
    const previewToken = text(preview?.preview_token);
    const optionToken = text(option?.option_token);
    if (!previewToken || !optionToken || option.selectable !== true) return null;
    const identifier = identifierOf(preview);
    const ticker = text(option.ticker);
    const country = text(option.country);
    const basketKey = text(option.basket_key);
    let key = `preview:${previewToken}`;
    if (basketKey) key = `security:${basketKey}`;
    else if (identifier.type && identifier.value) key = `identifier:${identifier.type}:${identifier.value}`;
    return Object.freeze({
        key,
        listingKey: basketKey ? `security:${basketKey}:${country}` : `option:${optionToken}`,
        kind: 'listing',
        label: ticker || `${identifierTypeLabel(identifier.type)} ${identifier.value}`.trim(),
        ticker,
        companyName: '',
        country,
        exchange: '',
        previewToken,
        optionTokens: Object.freeze([optionToken]),
        identifierType: identifier.type,
        identifierValue: identifier.value,
        ...cogniveIdField(option),
    });
}

/**
 * The pick that adds an identifier MSI has no listing for yet, to be looked
 * up on Apply like an uploaded row; null unless the lookup offered that.
 */
export function unresolvedPickFromPreview(preview, { requestedCountry = '' } = {}) {
    const previewToken = text(preview?.preview_token);
    const identifier = identifierOf(preview);
    if (
        preview?.resolve_on_apply_available !== true
        || !previewToken
        || !identifier.type
        || !identifier.value
    ) return null;
    const country = text(requestedCountry).toUpperCase();
    const key = `unresolved:${identifier.type}:${identifier.value}:${country}`;
    const ticker = identifier.type === 'ticker' ? identifier.value : '';
    return Object.freeze({
        key,
        listingKey: key,
        kind: 'unresolved',
        label: ticker || `${identifierTypeLabel(identifier.type)} ${identifier.value}`,
        ticker,
        companyName: '',
        country,
        exchange: '',
        previewToken,
        optionTokens: Object.freeze([]),
        identifierType: identifier.type,
        identifierValue: identifier.value,
        requestedCountry: country,
    });
}

function isPick(pick) {
    return Boolean(
        pick
        && typeof pick.key === 'string'
        && pick.key
        && typeof pick.previewToken === 'string'
        && pick.previewToken
        && Array.isArray(pick.optionTokens)
        && (pick.kind === 'unresolved' || (pick.kind === 'listing' && pick.optionTokens.length > 0)),
    );
}

function includesCogniveId(cogniveIds, cogniveId) {
    if (!cogniveId || !cogniveIds) return false;
    if (cogniveIds instanceof Set) return cogniveIds.has(cogniveId);
    return Array.isArray(cogniveIds) && cogniveIds.includes(cogniveId);
}

/**
 * How many more securities fit in the Watchlist once the picks are added.
 * `currentCount` is the number of securities already in it.
 */
export function remainingCapacity(picks, {
    maxSecurities = DEFAULT_MAX_SECURITIES,
    currentCount = 0,
} = {}) {
    const max = Number.isSafeInteger(maxSecurities) && maxSecurities >= 0
        ? maxSecurities
        : DEFAULT_MAX_SECURITIES;
    const current = Number.isSafeInteger(currentCount) && currentCount >= 0 ? currentCount : 0;
    return Math.max(0, max - current - picks.length);
}

/**
 * Add one pick. Returns { picks, outcome } with an outcome from PICK_OUTCOME;
 * `picks` is unchanged unless the outcome is ADDED or REPLACED. A replacement
 * or a new pick goes to the end, so the last pick is always the last entry.
 */
export function addPick(picks, pick, {
    maxSecurities = DEFAULT_MAX_SECURITIES,
    currentCount = 0,
    existingCogniveIds = null,
} = {}) {
    if (!isPick(pick)) return { picks, outcome: PICK_OUTCOME.INVALID };
    if (includesCogniveId(existingCogniveIds, pick.cogniveId)) {
        return { picks, outcome: PICK_OUTCOME.ALREADY_IN_WATCHLIST };
    }
    const index = picks.findIndex((existing) => existing.key === pick.key);
    if (index >= 0) {
        const existing = picks[index];
        // A fresh pick of the same listing renews an out-of-date one.
        if (existing.listingKey === pick.listingKey && !existing.stale) {
            return { picks, outcome: PICK_OUTCOME.DUPLICATE };
        }
        return {
            picks: [...picks.slice(0, index), ...picks.slice(index + 1), pick],
            outcome: PICK_OUTCOME.REPLACED,
        };
    }
    if (picks.length >= MAX_PICKS_PER_SAVE) return { picks, outcome: PICK_OUTCOME.SAVE_FIRST };
    if (remainingCapacity(picks, { maxSecurities, currentCount }) <= 0) {
        return { picks, outcome: PICK_OUTCOME.WATCHLIST_FULL };
    }
    return { picks: [...picks, pick], outcome: PICK_OUTCOME.ADDED };
}

export function removePick(picks, key) {
    const next = picks.filter((pick) => pick.key !== key);
    return next.length === picks.length ? picks : next;
}

/**
 * The undo step for one change of the picks (an addition, a replacement or a
 * removal), recorded in the owner's draft history beside cell edits
 * (draftHistory.js) so Undo reverses either kind in order: undo restores
 * `picks.before`, redo `picks.after`.
 */
export function pickHistoryAction(before, after, label) {
    return Object.freeze({ label, picks: Object.freeze({ before, after }) });
}

export function isPickHistoryAction(action) {
    return Boolean(action?.picks);
}

/**
 * Carry the stale marks Save just made (`previous` -> `next`, the result of
 * markStaleStageItems or markStalePicks) into a draft history, so no undo or
 * redo brings back a choice the server found out of date.
 */
export function markStalePicksInHistory(history, previous, next) {
    const staleCopies = new Map();
    previous.forEach((pick, index) => {
        if (next[index] !== pick) staleCopies.set(pick, next[index]);
    });
    if (!staleCopies.size) return history;
    const patch = (picks) => (
        picks.some((pick) => staleCopies.has(pick))
            ? picks.map((pick) => staleCopies.get(pick) || pick)
            : picks
    );
    const patchAction = (action) => {
        if (!isPickHistoryAction(action)) return action;
        const before = patch(action.picks.before);
        const after = patch(action.picks.after);
        return before === action.picks.before && after === action.picks.after
            ? action
            : pickHistoryAction(before, after, action.label);
    };
    return { past: history.past.map(patchAction), future: history.future.map(patchAction) };
}

export function isListingPick(pick) {
    return pick?.kind === 'listing';
}

/**
 * The `items` for stageSecurityResolutionAdditions: one per listing pick, in
 * pick order, with blank attributes (attribute cells of new rows unlock once
 * the security is live). The same payload the ticker basket stages.
 */
export function toStageItems(picks) {
    return picks.filter(isListingPick).map((pick) => ({
        preview_token: pick.previewToken,
        option_tokens: [...pick.optionTokens],
        field_drafts: {},
    }));
}

/**
 * One request per unresolved pick for stageSecurityResolutionUnresolvedAddition;
 * the caller adds attemptId and expectedWatchlistRevisionId.
 */
export function toUnresolvedStageRequests(picks) {
    return picks
        .filter((pick) => pick.kind === 'unresolved')
        .map((pick) => ({
            key: pick.key,
            previewToken: pick.previewToken,
            requestedCountry: pick.requestedCountry || undefined,
        }));
}

/**
 * Mark the listing picks behind out-of-date Stage items. Without valid
 * indices every listing pick is marked, as the ticker basket does.
 */
export function markStaleStageItems(picks, staleIndices) {
    const listingKeys = picks.filter(isListingPick).map((pick) => pick.key);
    const indices = new Set(
        (Array.isArray(staleIndices) ? staleIndices : []).filter((index) => (
            Number.isSafeInteger(index) && index >= 0 && index < listingKeys.length
        )),
    );
    const markAll = indices.size === 0;
    return markStalePicks(picks, listingKeys.filter((_key, index) => markAll || indices.has(index)));
}

/** Mark picks out of date by key, e.g. an unresolved pick whose lookup expired. */
export function markStalePicks(picks, keys) {
    const staleKeys = new Set(keys);
    if (!picks.some((pick) => staleKeys.has(pick.key) && !pick.stale)) return picks;
    return picks.map((pick) => (
        staleKeys.has(pick.key) && !pick.stale ? Object.freeze({ ...pick, stale: true }) : pick
    ));
}

/** Out-of-date picks must be removed or picked again before Save. */
export function hasStalePicks(picks) {
    return picks.some((pick) => pick.stale);
}
